import { doc, collection, setDoc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore'
import { db } from '@/firebase'
import { RULE_EXPECTATIONS, attempt, accessDetail, fmtExpected } from '../ruleExpectations.js'

// Live probe of the first-slice close model (Developer/vision/FEEDBACK_FORMS.md):
// completed is progress, not a lock. A submitted response stays writable while
// the form is completed and unclosed; responderClosedAt freezes it.
//
// Uses a general survey to test the shared close/edit behavior without a fake
// student link. Student-parent enforcement is tested separately in the rules suite.
// Creates a tagged instance assigned to the current user plus their own
// submitted response, then asserts both writes. Run as a case manager / admin
// / psych (roles that can create instances). Other roles pass the create
// denial and skip the lock probes.

export default {
  id: 'feedbackCloseLock',
  label: 'Feedback close lock (completed ≠ closed)',
  description:
    'Completed form still allows a teacher edit. Close now freezes it, except for super-admins (admin, school admin, 504 admin, district admin), whose response updates are not frozen.',
  destructive: true,

  async run(ctx, step) {
    const role = ctx.roleClaim
    const expectCreate = RULE_EXPECTATIONS.feedbackInstanceCreate(role)
    const instRef = doc(collection(db, 'feedbackInstances'))
    const respRef = doc(db, 'feedbackResponses', `${instRef.id}_${ctx.user.uid}`)
    const tag = { __smokeTest: true, __smokeRunId: ctx.runId }
    const school = ctx.schoolId ? { schoolId: ctx.schoolId } : {}
    let created = false

    try {
      await step(`create completed instance (expected: ${fmtExpected(expectCreate)})`, async () => {
        const { allowed } = await attempt(() =>
          setDoc(instRef, {
            assignedTeachers: [ctx.user.uid],
            teacherCanEditAfterSubmit: true,
            status: 'completed',
            responderCloseMode: 'manual',
            caseManagerId: ctx.user.uid,
            studentId: null,
            studentName: 'SMOKE Feedback',
            templateTitle: 'SMOKE close-lock',
            audienceType: 'general',
            createdAt: serverTimestamp(),
            ...tag,
            ...school
          })
        )
        created = allowed
        return allowed
          ? `${accessDetail(expectCreate, allowed)} — feedbackInstances/${instRef.id}`
          : accessDetail(expectCreate, allowed)
      })

      if (!created) {
        step.skip('close-lock probes', 'Role cannot create — later steps skipped')
        return
      }

      await step('create own submitted response (expected: allowed)', async () => {
        const { allowed } = await attempt(() =>
          setDoc(respRef, {
            instanceId: instRef.id,
            studentId: null,
            teacherId: ctx.user.uid,
            status: 'submitted',
            responses: { q1: { text: 'smoke-original' } },
            ...tag,
            ...school
          })
        )
        return accessDetail(true, allowed)
      })

      await step('edit submitted response on a completed form (expected: allowed)', async () => {
        const { allowed } = await attempt(() =>
          updateDoc(respRef, {
            responses: { q1: { text: 'smoke-revised-while-completed' } },
            status: 'submitted'
          })
        )
        return accessDetail(true, allowed)
      })

      await step('close the form (set responderClosedAt)', async () => {
        const { allowed } = await attempt(() =>
          updateDoc(instRef, { responderClosedAt: serverTimestamp() })
        )
        return accessDetail(true, allowed)
      })

      const expectAfterClose = RULE_EXPECTATIONS.feedbackEditAfterClose(role)
      await step(
        `edit submitted response after Close now (expected: ${fmtExpected(expectAfterClose)})`,
        async () => {
          const { allowed } = await attempt(() =>
            updateDoc(respRef, {
              responses: { q1: { text: 'smoke-after-close' } },
              status: 'submitted'
            })
          )
          return accessDetail(expectAfterClose, allowed)
        }
      )
    } finally {
      await deleteDoc(respRef).catch(() => {})
      await deleteDoc(instRef).catch(() => {})
    }
  }
}
